/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import type { EditorType } from '@ckeditor/ckeditor5-metadata';
import type { EditorPlugins } from '../../types/EditorPlugins';
import { isDecoupledEditor } from '../isDecoupledEditor/isDecoupledEditor';
import { isClassicEditor } from '../isClassicEditor/isClassicEditor';
import { isInlineEditor } from '../isInlineEditor/isInlineEditor';
import { isBalloonEditor } from '../isBalloonEditor/isBalloonEditor';
import { requiresDocumentOutlineContainer } from '../requiresDocumentOutlineContainer/requiresDocumentOutlineContainer';
import { requiresCommentsContainer } from '../requiresCommentsContainer/requiresCommentsContainer';
import { requiresMinimapContainer } from '../requiresMinimapContainer/requiresMinimapContainer';
import { requiresPaginationPlugin } from '../requiresPaginationPlugin/requiresPaginationPlugin';
import { classNames } from '../classNames/classNames';
import { requiresStylePlugin } from '../requiresStylePlugin/requiresStylePlugin';
import { requiresBlockToolbarPlugin } from '../requiresBlockToolbarPlugin/requiresBlockToolbarPlugin';
import { requiresWordCountPlugin } from '../requiresWordCountPlugin/requiresWordCountPlugin';
import { requiresFullscreenPlugin } from '../requiresFullscreenPlugin/requiresFullscreenPlugin';

export function getEditorContainerClassNames( editorType: EditorType, editorPlugins: EditorPlugins ): string {
	const isDecoupled = isDecoupledEditor( editorType );
	const isClassic = isClassicEditor( editorType );
	const isInline = isInlineEditor( editorType );
	const isBalloon = isBalloonEditor( editorType );
	const hasDocumentOutline = requiresDocumentOutlineContainer( editorPlugins );
	const hasAnnotations = requiresCommentsContainer( editorPlugins );
	const hasMinimap = requiresMinimapContainer( editorPlugins );
	const hasPagination = requiresPaginationPlugin( editorPlugins );
	const hasStyle = requiresStylePlugin( editorPlugins );
	const hasBlockToolbar = requiresBlockToolbarPlugin( editorPlugins ) && !isDecoupled;
	const hasWordCount = requiresWordCountPlugin( editorPlugins );
	const hasFullscreen = requiresFullscreenPlugin( editorPlugins );

	return classNames( {
		'editor-container': true,
		'editor-container_document-editor': isDecoupled,
		'editor-container_classic-editor': isClassic,
		'editor-container_inline-editor': isInline,
		'editor-container_balloon-editor': isBalloon,
		'editor-container_include-outline': hasDocumentOutline,
		'editor-container_include-annotations': hasAnnotations,
		'editor-container_include-minimap': hasMinimap,
		'editor-container_include-pagination': hasPagination,
		'editor-container_include-style': hasStyle,
		'editor-container_include-block-toolbar': hasBlockToolbar,
		'editor-container_include-word-count': hasWordCount,
		'editor-container_include-fullscreen': hasFullscreen
	} );
}
